/**
 * Horarios › Feriados (008 · T075 · US4; contracts/ui.md §1.5, spec US4 escenarios 1–3 y 8).
 *
 * - Filtros en borrador (QA-2): año (defecto: el de hoy en la zona de la empresa), sede
 *   ("Todas" = sin filtro; con una sede la API devuelve los de empresa y los de esa sede) y
 *   "Mostrar retirados", con "Buscar", "Limpiar filtros" y "Actualizar".
 * - Tabla (Fecha, Nombre, Alcance "Todas las sedes"/sede, Origen, Estado) ordenada por la API.
 * - "Nuevo feriado" y "Retirar" solo con `schedules.configure`: el Encargado de sede ve los
 *   feriados de empresa y de sus sedes, pero no tiene las acciones. Un feriado de integración se
 *   muestra con candado y no se retira aquí. Empresa suspendida ⇒ acciones deshabilitadas.
 */
import { PartyPopper, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { Holiday } from '@fermin/contracts';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardDescription, CardTitle } from '../../../components/ui/card';
import { EmptyState } from '../../../components/ui/empty-state';
import { Field } from '../../../components/ui/field';
import { FilterActions, FilterForm } from '../../../components/ui/filter-actions';
import { RefreshButton } from '../../../components/ui/refresh-button';
import { Select, type SelectOption } from '../../../components/ui/select';
import { SkeletonLines } from '../../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../../../components/ui/table';
import { t } from '../../../i18n';
import { useAppliedFilters } from '../../../lib/use-applied-filters';
import { managedLabel, ManagedSourceLabel, useIntegrationSystemName } from '../../integration/managed-by';
import { hasPermission, useMe } from '../../shell/use-me';
import { readOnlyProps, useReadOnly } from '../../shell/use-read-only';
import { useSites } from '../../sites/api';
import { scheduleErrorMessage, useHolidays, type HolidayFilters } from '../api';
import { todayInZone } from '../format';

import { HolidayFormDialog } from './holiday-form-dialog';
import { holidayDayLabel, holidayScopeLabel } from './holidays-format';
import { RemoveHolidayDialog } from './remove-holiday-dialog';

const ALL_SITES = 'ALL';

interface HolidayDraft {
  year: string;
  site_id: string;
  include_removed: boolean;
}

/** Años ofrecidos: el anterior, el actual y los dos siguientes. */
export function holidayYears(currentYear: number): number[] {
  return [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
}

/** Filtros de la API desde el borrador aplicado. */
export function holidayFiltersFrom(draft: HolidayDraft): HolidayFilters {
  return {
    year: Number(draft.year),
    ...(draft.site_id !== ALL_SITES ? { site_id: draft.site_id } : {}),
    ...(draft.include_removed ? { include_removed: true } : {}),
  };
}

export function HolidaysTab() {
  const me = useMe();
  // 009 · US6 (T055): los feriados integrados dicen "Gestionado por <sistema>".
  const systemName = useIntegrationSystemName();
  const { readOnly } = useReadOnly();
  const canConfigure = hasPermission(me.data, 'schedules.configure');
  const today = todayInZone(me.data?.company?.default_timezone ?? 'America/Lima');
  const currentYear = Number(today.slice(0, 4));

  const [defaults] = useState<HolidayDraft>(() => ({
    year: String(currentYear),
    site_id: ALL_SITES,
    include_removed: false,
  }));
  const { draft, applied, update, apply, clear, dirty, isFiltered, canClear } =
    useAppliedFilters<HolidayDraft>(defaults);
  const filters = useMemo(() => holidayFiltersFrom(applied), [applied]);
  const query = useHolidays(filters);
  const holidays = query.data ?? [];
  const sites = useSites({ status: 'ACTIVE' });
  const [createOpen, setCreateOpen] = useState(false);
  const [removing, setRemoving] = useState<Holiday | null>(null);

  const yearOptions: SelectOption[] = useMemo(
    () => holidayYears(currentYear).map((year) => ({ value: String(year), label: String(year) })),
    [currentYear],
  );
  const siteOptions: SelectOption[] = useMemo(
    () => [
      { value: ALL_SITES, label: t('schedules.holidays.filters.allSites') },
      ...(sites.data ?? []).map((site) => ({ value: site.id, label: site.name })),
    ],
    [sites.data],
  );

  // Al crear, la fecha inicial es hoy si el año aplicado es el actual; si no, el 1 de enero.
  const defaultDay = applied.year === String(currentYear) ? today : `${applied.year}-01-01`;

  return (
    <div className="flex flex-col gap-fm-4">
      <div className="flex flex-wrap items-center justify-between gap-fm-3">
        <p className="text-fm-sm text-fm-text-secondary">{t('schedules.holidays.description')}</p>
        <div className="flex flex-wrap items-center gap-fm-2">
          <RefreshButton query={query} />
          {canConfigure ? (
            <Button
              variant="compact"
              size="md"
              icon={<Plus size={20} />}
              data-testid="holiday-new"
              onClick={() => setCreateOpen(true)}
              {...readOnlyProps(readOnly)}
            >
              {t('schedules.holidays.new')}
            </Button>
          ) : null}
        </div>
      </div>

      <Card>
        <FilterForm onApply={apply} className="block">
          <fieldset className="flex flex-wrap items-end gap-fm-3">
            <legend className="sr-only">{t('schedules.holidays.filters.legend')}</legend>
            <Field label={t('schedules.holidays.filters.year')} className="w-[140px]">
              {(control) => (
                <Select
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  value={draft.year}
                  onValueChange={(year) => update({ year })}
                  options={yearOptions}
                />
              )}
            </Field>
            <Field label={t('schedules.holidays.filters.site')} className="w-[220px]">
              {(control) => (
                <Select
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  value={draft.site_id}
                  onValueChange={(site_id) => update({ site_id })}
                  options={siteOptions}
                />
              )}
            </Field>
            <label className="flex min-h-fm-touch items-center gap-fm-2 text-fm-sm text-fm-text">
              <input
                type="checkbox"
                className="size-4 accent-[var(--fm-primary)]"
                checked={draft.include_removed}
                onChange={(event) => update({ include_removed: event.target.checked })}
              />
              {t('schedules.holidays.filters.includeRemoved')}
            </label>
            <FilterActions onSearch={apply} dirty={dirty} canClear={canClear} onClear={clear} />
          </fieldset>
        </FilterForm>
      </Card>

      {query.isPending ? <SkeletonLines lines={4} /> : null}

      {query.isError ? (
        <Card>
          <CardTitle>{t('schedules.holidays.error.title')}</CardTitle>
          <CardDescription>{scheduleErrorMessage(query.error)}</CardDescription>
          <Button className="mt-fm-4" size="sm" onClick={() => void query.refetch()}>
            {t('common.buttons.retry')}
          </Button>
        </Card>
      ) : null}

      {query.isSuccess && holidays.length === 0 ? (
        <EmptyState
          icon={PartyPopper}
          title={
            isFiltered
              ? t('schedules.holidays.noResults.title')
              : t('schedules.holidays.empty.title', { year: applied.year })
          }
          description={
            isFiltered
              ? t('schedules.holidays.noResults.description')
              : t('schedules.holidays.empty.description')
          }
        />
      ) : null}

      {holidays.length > 0 ? (
        <Table data-testid="holidays-table">
          <TableCaption>{t('schedules.holidays.caption', { year: applied.year })}</TableCaption>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t('schedules.holidays.columns.day')}</TableHeaderCell>
              <TableHeaderCell>{t('schedules.holidays.columns.name')}</TableHeaderCell>
              <TableHeaderCell>{t('schedules.holidays.columns.scope')}</TableHeaderCell>
              <TableHeaderCell>{t('schedules.holidays.columns.source')}</TableHeaderCell>
              <TableHeaderCell>{t('schedules.holidays.columns.status')}</TableHeaderCell>
              <TableHeaderCell>
                <span className="sr-only">{t('schedules.holidays.columns.actions')}</span>
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {holidays.map((holiday) => {
              const integration = holiday.source === 'INTEGRATION';
              const removable = canConfigure && holiday.status === 'ACTIVE' && !integration;
              return (
                <TableRow key={holiday.id} data-testid="holiday-row" data-status={holiday.status}>
                  <TableCell className="fm-tabular">{holidayDayLabel(holiday.day)}</TableCell>
                  <TableCell className="font-semibold">{holiday.name}</TableCell>
                  <TableCell>{holidayScopeLabel(holiday)}</TableCell>
                  <TableCell>
                    <ManagedSourceLabel source={holiday.source} systemName={systemName} />
                  </TableCell>
                  <TableCell>
                    {holiday.status === 'ACTIVE' ? (
                      <Badge variant="activa">{t('schedules.holidays.status.ACTIVE')}</Badge>
                    ) : (
                      <Badge variant="cancelada">{t('schedules.holidays.status.REMOVED')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {removable ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="holiday-remove"
                        aria-label={t('schedules.holidays.removeLabel', { name: holiday.name })}
                        onClick={() => setRemoving(holiday)}
                        {...readOnlyProps(readOnly)}
                      >
                        {t('schedules.holidays.remove')}
                      </Button>
                    ) : integration && holiday.status === 'ACTIVE' ? (
                      <span className="sr-only">
                        {t('schedules.managed.holidayLocked', { system: managedLabel(systemName) })}
                      </span>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : null}

      {canConfigure ? (
        <>
          <HolidayFormDialog
            open={createOpen}
            onOpenChange={setCreateOpen}
            sites={sites.data ?? []}
            defaultDay={defaultDay}
          />
          <RemoveHolidayDialog holiday={removing} onClose={() => setRemoving(null)} />
        </>
      ) : null}
    </div>
  );
}
